import { useState } from 'react';
import dayjs from 'dayjs';

import { cashKey } from '@/api/cash';
import { useApiMutation } from '@/api/mutations';
import { purchaseOrdersKey } from '@/api/purchaseOrders';
import { outstandingKey } from '@/api/outstanding';
import { purchaseBillsKey } from '@/api/purchaseBills';
import { purchaseDeskKey } from '@/api/purchaseDesk';
import { useVendorLedger, useVendorPaymentAccounts, vendorPaymentsKey } from '@/api/vendorPayments';
import { DocumentHeading } from '@/components/DocumentSheet';
import { enterKeyScope, useAutoFocusFirstField } from '@/components/enterKeyNavigation';
import { Button, Card, Notice } from '@/components/PageHeading';
import { QuickAmounts, SplitBoxes } from '@/components/SplitBoxes';
import { useSplitParts } from '@/components/useSplitParts';
import { payVendorInParts } from '@core/api/purchaseCommands';
import { paymentMethodLabel } from '@core/bookings/paymentWords';
import {
  fillOne,
  filledParts,
  splitProblem,
  splitTotal,
  splitWords,
  withoutSaved,
  type SplitPart,
} from '@core/cash/split';
import { formatAmount } from '@core/format/money';
import { quickPayments, settles } from '@core/purchasing/desk';
import type { Session } from '@core/session/types';

const rupees = (value: number) => `₹${formatAmount(value)}`;
const methodWord = (method: SplitPart['method']) =>
  method === 'Cash' ? 'cash' : paymentMethodLabel[method];

/**
 * Purchases › Pay — **a box per account, the total adds itself up** (owner, 2026-11-01, canvas
 * "Supplier payment and choice", Pay B; the phone's Pay a supplier on the same core `cash/split.ts`).
 * ₹6,000 against the till and ₹4,000 by UPI into HDFC is two payments, each from its own account, so
 * each day book is right; a bank's row says how and takes a reference. The quick amounts fill one
 * box. Beside each bill, whether the total pays it off — set against the oldest first, the server's
 * rule. "Set it against bills myself" opens the full Pay a vendor page, one account at a time.
 */
export function PayScreen({
  session,
  vendorId,
  onClose,
  onDone,
  onSplit,
}: {
  session: Session;
  vendorId: number;
  onClose: () => void;
  onDone: () => void;
  onSplit: () => void;
}) {
  const ledger = useVendorLedger(vendorId);
  const accounts = useVendorPaymentAccounts(session.branch?.id);
  const { parts, change: setParts } = useSplitParts(accounts.data);
  const [tried, setTried] = useState(false);
  const [partly, setPartly] = useState<string | null>(null);
  const fields = useAutoFocusFirstField<HTMLDivElement>();

  const total = splitTotal(parts);
  const used = filledParts(parts).length;
  const problem = splitProblem(parts);

  const pay = useApiMutation({
    mutationFn: () => payVendorInParts(vendorId, dayjs().format('YYYY-MM-DD'), parts),
    success: ({ saved, failed }) =>
      failed
        ? `Part paid — ${saved.length} of ${saved.length + 1} parts went through.`
        : saved.length === 1
          ? `${saved[0]!.result.documentNumber} — ${rupees(saved[0]!.result.amount)} paid.`
          : `${saved.length} payments, ${rupees(total)} in all, paid.`,
    invalidates: [
      vendorPaymentsKey,
      purchaseBillsKey,
      purchaseOrdersKey,
      purchaseDeskKey,
      outstandingKey,
      cashKey,
    ],
    onSuccess: ({ saved, failed }) => {
      if (!failed) {
        onDone();
        return;
      }

      // What went through is emptied, so pressing Pay again sends only what is left.
      setParts((current) => withoutSaved(current, saved));
      const words = splitWords(
        saved.map((each) => each.part),
        methodWord,
        rupees,
        'from',
      );
      setPartly(`${words} — paid. ${failed.part.accountName} was not.`);
    },
  });
  const busy = pay.isPending;

  if (!ledger.data) {
    return null;
  }

  const data = ledger.data;
  const open = data.dues.filter((due) => due.outstanding > 0);
  const settled = settles(data, total);

  const submit = () => {
    setTried(true);
    setPartly(null);

    if (problem) {
      return;
    }

    void pay.mutateAsync().catch(() => undefined);
  };

  return (
    <>
      <DocumentHeading
        trail={data.vendorName}
        current="Pay"
        title={`Pay ${data.vendorName}`}
        onBack={onClose}
      />

      <div ref={fields} {...enterKeyScope} className="grid grid-cols-12 gap-4">
        <div className="col-span-12 xl:col-span-7">
          <Card>
            <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-default mb-0 text-[length:var(--scale-list-name)]">
                Owed {rupees(data.owed)} on {open.length} bill{open.length === 1 ? '' : 's'} ·{' '}
                {data.onAccount > 0 ? `${rupees(data.onAccount)} on account` : 'nothing on account'}
              </p>
              <p className="mb-0 text-end">
                <span className="font-serif text-2xl font-semibold text-gray-900">
                  {rupees(total)}
                </span>
                <span className="text-default ms-2 text-[length:var(--scale-list-meta)]">
                  {used === 0
                    ? 'type it against the account it is paid from'
                    : `from ${used} ${used === 1 ? 'account' : 'accounts'}`}
                </span>
              </p>
            </div>

            {accounts.isError ? (
              <Notice tone="danger" title="The accounts could not be read." />
            ) : parts.length === 0 && accounts.isSuccess ? (
              <Notice tone="warning" title="No account can pay from this branch.">
                Add one in Cash and bank.
              </Notice>
            ) : null}

            <SplitBoxes parts={parts} onChange={setParts} direction="from" busy={busy} />

            <div className="mt-3">
              <QuickAmounts
                options={quickPayments(data)}
                busy={busy}
                onPick={(amount) => setParts((current) => fillOne(current, amount))}
              />
            </div>
          </Card>
        </div>

        <div className="col-span-12 xl:col-span-5">
          <Card>
            <p className="mb-2 text-[length:var(--scale-list-name)] font-semibold text-gray-900">
              This payment settles
            </p>
            <ul className="m-0 flex list-none flex-col ps-0">
              {open
                .slice()
                .sort((a, b) => a.dueDate.localeCompare(b.dueDate) || a.id - b.id)
                .map((due) => {
                  const result = settled.find((s) => s.id === due.id && s.kind === due.kind);

                  return (
                    <li
                      key={`${due.kind}-${due.id}`}
                      className="border-border-color flex list-none items-center justify-between gap-3 border-b py-2 last:border-b-0"
                    >
                      <div>
                        <p className="mb-0 text-xs font-semibold text-gray-900">
                          {due.description}
                        </p>
                        <p className="text-default mb-0 text-[11px]">
                          {due.reference} · due {dayjs(due.dueDate).format('D MMM')} ·{' '}
                          {rupees(due.outstanding)} left
                        </p>
                      </div>
                      <span
                        className={`text-end text-[11px] font-semibold ${
                          result?.paidOff
                            ? 'text-[color:var(--color-success)]'
                            : result && result.settled > 0
                              ? 'text-gray-900'
                              : 'text-default'
                        }`}
                      >
                        {result?.paidOff
                          ? 'Paid off ✓'
                          : result && result.settled > 0
                            ? `${rupees(result.settled)} of it`
                            : 'Still owed'}
                      </span>
                    </li>
                  );
                })}
            </ul>
            <button
              type="button"
              disabled={busy}
              onClick={onSplit}
              className="text-primary mt-2 text-[length:var(--scale-list-meta)] hover:underline"
            >
              Set it against bills myself
            </button>

            <div className="mt-4 flex flex-col gap-2">
              {partly ? (
                <Notice tone="warning" title="Part of the payment went through">
                  {partly}
                </Notice>
              ) : null}
              {tried && problem ? (
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">{problem}</p>
              ) : null}
              {used > 0 ? (
                <p className="text-default mb-0 text-[length:var(--scale-list-meta)]">
                  {splitWords(parts, methodWord, rupees, 'from')}.
                </p>
              ) : null}
              <Button
                variant="primary"
                icon="icon icon-check"
                data-enter-key-commit
                shortcut="save"
                loading={busy}
                disabled={busy || session.branch === null}
                onClick={submit}
              >
                {busy
                  ? 'Paying…'
                  : used > 1
                    ? `Pay ${rupees(total)} from ${used} accounts`
                    : used === 1
                      ? `Pay ${rupees(total)}`
                      : 'Pay'}
              </Button>
              <Button variant="secondary" disabled={busy} onClick={onClose}>
                Cancel
              </Button>
            </div>
            <p className="text-default mt-2 mb-0 text-center text-[11px]">
              Each account's part goes into the cash book as money out, today.
            </p>
          </Card>
        </div>
      </div>
    </>
  );
}
